import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Box,
  LinearProgress,
  Stack,
  Paper,
  Chip,
  Divider,
  Alert,
  Grid,
  Avatar,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { db } from '../firebase';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';

export default function NewProjectPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [projectCode, setProjectCode] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [organisation, setOrganisation] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState('');

  const helperSteps = [
    {
      id: 1,
      title: 'Dosyanızı yükleyin',
      description: 'Excel (.xlsx) veya SPSS (.sav) dosyanızı bilgisayarınızdan seçin.',
      icon: CloudUploadIcon,
    },
    {
      id: 2,
      title: 'Klasör belirleyin',
      description: 'Projeyi doğru klasör altında tutarak ekibinizle paylaşmayı kolaylaştırın.',
      icon: FolderOpenIcon,
    },
    {
      id: 3,
      title: 'Yükleme tamamlanana kadar bekleyin',
      description: 'Yükleme sırasında sayfayı kapatmayın; ilerlemeyi aşağıdan takip edebilirsiniz.',
      icon: ScheduleIcon,
    },
  ];

  const surfaceBaseSx = {
    borderRadius: 4,
    border: '1px solid rgba(148, 163, 184, 0.18)',
    backgroundColor: '#ffffff',
    boxShadow: '0 20px 36px rgba(148, 163, 184, 0.24)',
    px: { xs: 2.5, md: 4 },
    py: { xs: 3, md: 4 },
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.4)' },
      '&:hover fieldset': { borderColor: '#3b82f6' },
      '&.Mui-focused fieldset': {
        borderColor: '#2563eb',
        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.2)',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: '#475569',
    },
  };

  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    px: { xs: 2.5, md: 3 },
    py: 1,
    borderRadius: 999,
    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    boxShadow: '0 16px 28px rgba(99, 102, 241, 0.24)',
    '&:hover': {
      background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
      boxShadow: '0 20px 32px rgba(79, 70, 229, 0.3)',
    },
  };

  const assignProjectToFolder = React.useCallback(
    async ({ projectCode: code, folderId }) => {
      if (!folderId) return true;
      if (!organisation) return false;

      const trimmedCode = (code || '').trim();
      if (!trimmedCode) return false;

      const projectsCol = collection(db, 'organisations', organisation, 'projects');
      const projectQuery = query(projectsCol, where('projectCode', '==', trimmedCode), limit(1));
      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      for (let attempt = 0; attempt < 8; attempt += 1) {
        try {
          const snap = await getDocs(projectQuery);
          if (!snap.empty) {
            const docSnap = snap.docs[0];
            const currentFolder = docSnap.data()?.folderID || '';
            if (currentFolder === folderId) return true;
            await updateDoc(doc(db, 'organisations', organisation, 'projects', docSnap.id), {
              folderID: folderId,
            });
            return true;
          }
        } catch (err) {
          console.error('assignProjectToFolder error', err);
          return false;
        }
        await sleep(800);
      }

      console.warn('assignProjectToFolder: project not found for code', trimmedCode);
      return false;
    },
    [organisation]
  );

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(''); // Hata mesajını temizle
    }
  };

  // Load organisation and folders, and default folder from navigation state if provided
  React.useEffect(() => {
    (async () => {
      try {
        if (!currentUser?.uid) return;
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const org = userSnap.data()?.organisation || '';
        setOrganisation(org);

        // default folder from navigation state
        const defFolder = location?.state?.defaultFolderId ?? '';
        if (defFolder) setSelectedFolderId(defFolder);

        if (!org) return;

        const q = query(collection(db, 'organisations', org, 'folders'), orderBy('createDate', 'desc'));
        return onSnapshot(q, (snap) => {
          const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          // Backfill missing createDate on folders
          snap.docs.forEach((d) => {
            const data = d.data();
            if (!data.createDate) {
              updateDoc(doc(db, 'organisations', org, 'folders', d.id), {
                createDate: serverTimestamp(),
              }).catch(() => {});
            }
          });
          setFolders(arr);
        });
      } catch (e) {
        console.error('load org/folders failed', e);
      }
    })();
  }, [currentUser?.uid]);

  // Form Gönderme
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!projectCode || !file) {
      setError(t('fillAllFields') || 'Please fill all fields');
      return;
    }

    if (selectedFolderId && !organisation) {
      setError(
        t('organisationMissingForFolder', {
          defaultValue: 'Organizasyon bilgisi yüklenmeden klasör atanamaz. Lütfen sayfayı yenileyin.',
        })
      );
      return;
    }
    
    setLoading(true);


    const fileName = file.name;
    const fileExtension = fileName.slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2);
    const normalizedExt = (fileExtension || '').toLowerCase();
    if (!['xlsx', 'sav'].includes(normalizedExt)) {
      setLoading(false);
      setError(t('unsupportedFileType') || 'Only .xlsx or .sav files are supported');
      return;
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("useremail", currentUser.email);
      formDataUpload.append("fileExtension", normalizedExt);

      const response = await fetch("https://reporting-upload-file-795950092325.europe-west3.run.app", {
        method: "POST",
        body: formDataUpload
      });
      
      const data = await response.json();
      
      // Upload with progress tracking
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      };

      await new Promise((resolve, reject) => {
        xhr.open("PUT", data.signed_url);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.onload = () => resolve(xhr);
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });

      if (xhr.status !== 200) throw new Error('Upload failed');


      const formData = new FormData();
      formData.append('projectCode', projectCode);
      formData.append('new_file_name', data.new_file_name);
      formData.append('useremail', currentUser.email);
      if (selectedFolderId) {
        formData.append('folderID', selectedFolderId);
      } else {
        formData.append('folderID', '');
      }

      try {
        const response = await fetch('https://reporting-create-project-0-795950092325.europe-west3.run.app', {
          method: 'POST',
          body: formData,
        });
        if (!response.ok) throw new Error(t('createProjectFailed') || 'Project creation failed');
      } catch (err) {
        setError(err.message);
        return;
      }

      if (selectedFolderId) {
        const assigned = await assignProjectToFolder({
          projectCode,
          folderId: selectedFolderId,
        });
        if (!assigned) {
          setError(
            t('folderAssignmentFailed', {
              defaultValue:
                'Proje oluşturuldu fakat klasöre taşınamadı. Lütfen birkaç saniye sonra tekrar deneyin.',
            })
          );
          return;
        }
      }

      navigate('/ProjectsPage', { state: { resetProjectCode: projectCode.trim() } });

      } catch (error) {
        console.error('Error:', error);
        const uploadErrorMsg = t('uploadError') || 'Yükleme sırasında bir hata oluştu.';
        setError(uploadErrorMsg);
        alert(uploadErrorMsg);
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fbff 0%, #eef2ff 50%, #fdf2f8 100%)',
        py: { xs: 3, md: 4.5 },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 }, pb: { xs: 4, md: 6 } }}>
        <Stack spacing={2.5}>
          <Box
            sx={{
              borderRadius: 4,
              color: '#0f172a',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              background: 'linear-gradient(135deg, rgba(191, 219, 254, 0.9) 0%, rgba(244, 219, 255, 0.9) 100%)',
              boxShadow: '0 20px 40px rgba(148, 163, 184, 0.25)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ px: { xs: 2.5, md: 3.5 }, py: { xs: 2.5, md: 3.25 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Box>
                  <Typography variant="overline" sx={{ letterSpacing: 1.1, color: 'rgba(15,23,42,0.7)' }}>
                    {t('newProject')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1.5 }}>
                    {t('newProjectLead', { defaultValue: 'Yeni veri setinizi birkaç adımda içeri aktarın.' })}
                  </Typography>
                  <Typography variant="body1" sx={{ maxWidth: 520, color: 'rgba(15, 23, 42, 0.72)' }}>
                    {t(
                      'newProjectSubLead',
                      {
                        defaultValue:
                          'Dosyanızı yükleyin, doğru klasörü seçin ve raporlama ekranında yerini hemen alın.',
                      },
                    )}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 2 }}>
                    <Chip
                      icon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                      label=".xlsx • .sav"
                      sx={{
                        bgcolor: 'rgba(224, 231, 255, 0.7)',
                        color: '#1f2937',
                        fontWeight: 600,
                        borderRadius: 999,
                        '& .MuiChip-icon': { color: '#2563eb' },
                      }}
                    />
                    <Chip
                      icon={<AssignmentTurnedInIcon sx={{ fontSize: 16 }} />}
                      label={t('createProject')}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.6)',
                        color: '#0f172a',
                        fontWeight: 600,
                        borderRadius: 999,
                        '& .MuiChip-icon': { color: '#0f172a' },
                      }}
                    />
                  </Stack>
                </Box>
                <Stack spacing={1} alignItems={{ xs: 'stretch', md: 'flex-end' }} sx={{ width: { xs: '100%', md: 'auto' } }}>
                  <Button variant="outlined" onClick={() => navigate('/ProjectsPage')} sx={{ textTransform: 'none' }}>
                    {t('cancel')}
                  </Button>
                  <Typography variant="caption" sx={{ opacity: 0.75, maxWidth: 240, textAlign: { xs: 'left', md: 'right' } }}>
                    {t('backToDashboardHint', { defaultValue: 'Üzerinde çalıştığınız projelere geri dönmek için iptal edebilirsiniz.' })}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.5 } }}>
              <Grid container spacing={1} alignItems="stretch">
                <Grid item xs={12} md={7}>
                  <Paper sx={surfaceBaseSx}>
                    <Box component="form" onSubmit={handleSubmit}>
                      <Stack spacing={3}>
                        <TextField
                          label={t('projectCode')}
                          value={projectCode}
                          onChange={(e) => {
                            setProjectCode(e.target.value);
                            setError('');
                          }}
                          fullWidth
                          required
                          sx={textFieldSx}
                        />
                        <TextField
                          select
                          label="Klasör"
                          value={selectedFolderId}
                          onChange={(e) => setSelectedFolderId(e.target.value)}
                          fullWidth
                          SelectProps={{ native: true }}
                          sx={textFieldSx}
                        >
                          <option value="">My Projects</option>
                          {folders.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.title || 'Klasör'}
                            </option>
                          ))}
                        </TextField>

                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                            {t('selectFile')}
                          </Typography>
                          <Box
                            component="label"
                            htmlFor="file-input"
                            sx={{
                              display: 'block',
                              border: '2px dashed rgba(96,165,250,0.45)',
                              borderRadius: 3,
                              backgroundColor: 'rgba(248, 250, 252, 0.75)',
                              px: { xs: 2, md: 3 },
                              py: { xs: 2.5, md: 3 },
                              textAlign: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: '#2563eb',
                                backgroundColor: 'rgba(219, 234, 254, 0.75)',
                                boxShadow: '0 0 0 4px rgba(37, 99, 235, 0.12)',
                              },
                            }}
                          >
                            <Stack spacing={1} alignItems="center">
                              <CloudUploadIcon sx={{ fontSize: 32, color: '#2563eb' }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {file ? t('changeFile', { defaultValue: 'Dosyayı değiştir' }) : t('selectFile')}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(15, 23, 42, 0.7)' }}>
                                {file
                                  ? file.name
                                  : t('dropOrClick', { defaultValue: 'Bilgisayarınızdan seçmek için tıklayın' })}
                              </Typography>
                              <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
                                <Chip label=".xlsx" size="small" sx={{ bgcolor: 'rgba(59,130,246,0.12)', color: '#1d4ed8' }} />
                                <Chip label=".sav" size="small" sx={{ bgcolor: 'rgba(16,185,129,0.12)', color: '#047857' }} />
                              </Stack>
                            </Stack>
                          </Box>
                          <input
                            id="file-input"
                            type="file"
                            hidden
                            accept=".xlsx,.sav"
                            onChange={handleFileChange}
                          />
                          {file && file.name.toLowerCase().endsWith('.sav') && (
                            <Typography variant="body2" sx={{ mt: 1, color: 'rgba(15, 23, 42, 0.7)' }}>
                              SPSS (.sav) formatı algılandı.
                            </Typography>
                          )}
                        </Box>

                        {error && (
                          <Alert severity="error" sx={{ borderRadius: 3 }}>
                            {error}
                          </Alert>
                        )}

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <Button type="submit" variant="contained" disabled={loading} sx={primaryButtonSx}>
                            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('createProject')}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => navigate('/ProjectsPage')}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
                          >
                            {t('cancel')}
                          </Button>
                        </Stack>

                        {selectedFolderId && (
                          <Typography variant="caption" sx={{ display: 'block', color: 'rgba(15, 23, 42, 0.7)' }}>
                            Bu veri seti seçtiğiniz klasöre eklenecek.
                          </Typography>
                        )}

                        {uploadProgress > 0 && (
                          <Box
                            sx={{
                              borderRadius: 3,
                              border: '1px solid rgba(59, 130, 246, 0.24)',
                              backgroundColor: 'rgba(219, 234, 254, 0.45)',
                              px: 2,
                              py: 2,
                            }}
                          >
                            <LinearProgress variant="determinate" value={uploadProgress} />
                            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                              <Typography variant="caption" sx={{ color: '#1d4ed8', fontWeight: 600 }}>
                                %{uploadProgress}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(15, 23, 42, 0.7)' }}>
                                {t('doNotClosePage')}
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Paper
                    sx={{
                      ...surfaceBaseSx,
                      background: 'linear-gradient(155deg, rgba(255,255,255,0.92) 0%, rgba(226,232,240,0.88) 100%)',
                    }}
                  >
                    <Stack spacing={2.5}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(51,65,85,0.7)' }}>
                          Akış
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          Üç adımda yeni proje
                        </Typography>
                      </Box>
                      <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.28)' }} />
                      <Stack spacing={2.25}>
                        {helperSteps.map((step) => {
                          const StepIcon = step.icon;
                          return (
                            <Stack key={step.id} direction="row" spacing={1.5} alignItems="flex-start">
                              <Avatar
                                sx={{
                                  width: 44,
                                  height: 44,
                                  bgcolor: 'rgba(96, 165, 250, 0.18)',
                                  color: '#1d4ed8',
                                }}
                              >
                                <StepIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                  {step.title}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'rgba(15, 23, 42, 0.72)' }}>
                                  {step.description}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        })}
                      </Stack>
                      <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.24)' }} />
                      <Stack spacing={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          İpucu
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(15, 23, 42, 0.7)' }}>
                          Büyük dosyalar için yükleme birkaç dakika sürebilir. Tarayıcıyı kapatmayın ve internet bağlantınızı
                          sabit tutun.
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
        </Stack>
      </Container>
    </Box>
  );
}
